import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import dayjs from 'dayjs';

import { useTransactionsProvider } from '@/common/providers/TransactionsProvider/provider.tsx';
import { Table } from '@/common/components/Table/component.tsx';
import { currencyFormat, minifyString } from '@dor/utils';
import { PageTitle } from '@/common/components/PageTitle/component.tsx';
import { ViewLayout } from '@/common/components/ViewLayout/component.tsx';
import { InfoCard } from '@/common/components/InfoCard/component.tsx';
import { useDevicesProvider } from '@/common/providers/DevicesProvider/provider.tsx';
import { useSnapshotsProvider } from '@/common/providers/SnapshotsProvider/provider.tsx';
import {
  DeploymentDAGExplorerBaseUrl,
  DeploymentMetagraphId
} from '@/common/consts/api.ts';

import styles from './view.module.scss';

export const SnapshotDetailView = () => {
  const { ordinal } = useParams();

  const snapshots = useSnapshotsProvider();
  const devices = useDevicesProvider();
  const transactions = useTransactionsProvider();

  useEffect(() => {
    snapshots.snapshot.fetch({ ordinal: parseInt(ordinal ?? '0') }, {});
    devices.checkins.fetch(
      {},
      { ordinal: parseInt(ordinal ?? '') },
      { pageSize: 10 }
    );
    transactions.transactions.fetch(
      {},
      { ordinal: parseInt(ordinal ?? '') },
      { pageSize: 10 }
    );
  }, []);

  return (
    <ViewLayout>
      <PageTitle>Snapshot Details</PageTitle>
      <InfoCard
        records={[
          {
            label: 'Hash',
            value: minifyString(
              snapshots.snapshot.resource.resource?.hash ?? '',
              12,
              12
            )
          },
          {
            label: 'Latest Snapshot Hash',
            value: minifyString(
              snapshots.snapshot.resource.resource?.lastSnapshotHash ?? '',
              12,
              12
            )
          },
          { label: 'Ordinal', value: ordinal },
          {
            label: 'Timestamp',
            value: snapshots.snapshot.resource.resource?.timestamp
              ? dayjs(snapshots.snapshot.resource.resource?.timestamp).fromNow()
              : ''
          }
        ]}
      />
      <div className={styles.information}>
        <Table
          title="Device Check-ins"
          columns={[
            { key: 'deviceId', title: 'device id', style: 'link' },
            { key: 'timestamp', title: 'timestamp' },
            { key: 'dataHash', title: 'Data Hash' },
            {
              key: 'ordinal',
              title: 'ordinal',
              style: 'link'
            }
          ]}
          data={devices.checkins.resource.resource.map((checkin) => ({
            timestamp: (
              <span title={dayjs.unix(checkin.dts).toISOString()}>
                {dayjs.unix(checkin.dts).fromNow()}
              </span>
            ),
            deviceId: (
              <Link to={`/devices/${checkin.proof.id}`}>
                {minifyString(checkin.proof.id)}
              </Link>
            ),
            dataHash: minifyString(checkin.checkInHash),
            ordinal: (
              <Link to={`/snapshots/${checkin.ordinal}`}>
                {checkin.ordinal}
              </Link>
            )
          }))}
        />
        <Table
          className={styles.fullWidth}
          title="DOR Transactions"
          columns={[
            { key: 'trxHash', title: 'Txn Hash', style: 'link' },
            { key: 'timestamp', title: 'timestamp' },
            { key: 'snapshotHash', title: 'snapshot', style: 'link' },
            { key: 'trxFee', title: 'Fee' },
            { key: 'fromAddress', title: 'From', style: 'link' },
            { key: 'toAddres', title: 'To', style: 'link' },
            { key: 'amount', title: 'Amount' }
          ]}
          data={transactions.transactions.resource.resource.map(
            (transaction) => ({
              trxHash: (
                <a
                  href={`${DeploymentDAGExplorerBaseUrl}/metagraphs/${DeploymentMetagraphId}/transactions/${transaction.hash}`}
                  target="_blank"
                >
                  {minifyString(transaction.hash, 12, 12)}
                </a>
              ),
              timestamp: dayjs.utc(transaction.updatedAt).fromNow(),
              snapshotHash: (
                <Link to={`/snapshots/${transaction.snapshotOrdinal}`}>
                  {transaction.snapshotOrdinal}
                </Link>
              ),
              trxFee: currencyFormat('DOR', transaction.fee, 'DECIMAL', 8),
              fromAddress: (
                <a
                  href={`${DeploymentDAGExplorerBaseUrl}/address/${transaction.source}`}
                  target="_blank"
                >
                  {minifyString(transaction.source, 5, 5)}
                </a>
              ),
              toAddres: (
                <a
                  href={`${DeploymentDAGExplorerBaseUrl}/address/${transaction.destination}`}
                  target="_blank"
                >
                  {minifyString(transaction.destination, 5, 5)}
                </a>
              ),
              amount: currencyFormat('DOR', transaction.amount, 'DECIMAL', 8)
            })
          )}
        />
      </div>
    </ViewLayout>
  );
};
